import React, { createRef, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { FullscreenLayout } from 'src/components/widgets/fullscreenIframeLayout';
// LE VOCI DEL RAIL PORTANO I GLIFI MATERIAL, quelli del resto del Backoffice: il Composer li usa
// già, e due famiglie di disegni nella stessa barra si leggono come due barre.
import { T } from '../../../../../shared/helpers';
import { useElementSize } from '../../../../../shared/hooks/useElementSize';
import { useSubscriptionData } from '../../../../../shared/hooks/useSubscriptionData';
import { Dialog, useDialogManager } from '../../../../widgets/dialog';
import { removeNotification, showErrorNotification, showInfoNotification, showSuccessNotification } from '../../../../widgets/notificationToaster';
import { useProductConfigurationState } from '../../state';
import HeaderToolbar from '../configurator-components/headerToolbar';
import LeftVerticalToolbar, { SectionRail, StepsTools } from '../configurator-components/leftVerticalToolbar';
import CameraEditor from '../configurator-components/scene-editors/cameraEditor';
import LightsEditor from '../configurator-components/scene-editors/lightsEditor';
import MaterialsEditor from '../configurator-components/scene-editors/materialsEditor';
import { commitFinishChoices } from '../configurator-components/scene-editors/MaterialOverlaySimple';
import { materialsWithoutPreview, shootPreviews } from '../configurator-components/scene-editors/materialPreview';
import ObjectEditor from '../configurator-components/scene-editors/objectEditor';
import { VTOProviderSync } from '../configurator-components/scene-editors/vtoEditor';
import TexturesEditor from '../configurator-components/scene-editors/texturesEditor';
import EnvironmentsEditor from '../configurator-components/scene-editors/environmentsEditor';
import WorldEditor from '../configurator-components/scene-editors/worldEditor';
import EffectsEditor from '../configurator-components/scene-editors/effectsEditor';
import { SHOW_NOT_YET_AVAILABLE } from '../configurator-components/scene-editors/notYetAvailable';
import { TreeHandle } from '../configurator-components/TreeComponent';
import SceneEditorSettingsDialog from '../configurator-components/sceneEditorSettingsDialog';
import SceneEditorLibraryDrawer from './sceneEditorLibraryDrawer';
import { SectionPlacementProvider } from '../configurator-components/scene-editors/sectionPanel';
import { InspectorChromeProvider } from '../configurator-components/scene-editors/inspectorShell';
import { useMovableHost } from './useMovableHost';

// Relay generated types
import useSaveProductAndScene from 'src/components/widgets/preview3D/hooks/useSaveProduct';
import usePreview3DStore, { Preview3DStoreContext } from 'src/components/widgets/preview3D/store';
import { Load3DSceneEditor } from '../../../../widgets/preview3D';
import { EditorLoadingCard } from '../configurator-components/editorLoadingCard';
import SceneEditorView from '../../../../widgets/preview3D/sceneEditorView';
import { SceneEditorToolbar } from '../configurator-components/scene-editors/toolbar';
import { AssetBar, Panel, Resizer, StageColumn, Workspace } from '../../../../widgets/workspaceLayout/workspaceLayout';
import { NotificationBanner } from '../../../../widgets/notificationBanner';
import { getWorkspaceRailWidth, useWorkspacePreferences } from './useWorkspacePreferences';
import { applySelectionHighlight, SelectionHighlight } from './selectionHighlight';
import { useSceneBackground } from './useSceneBackground';
import { useEscapeClosesModals } from '../../../../widgets/hooks/useEscapeToClose';
import {
    CamerasRailIcon as CamerasIcon,
    EffectsRailIcon as EffectsIcon,
    LightsRailIcon as LightsIcon,
    MaterialsRailIcon as MaterialsIcon,
    ObjectsRailIcon as ObjectsIcon,
    WorldRailIcon as WorldIcon
} from '../configurator-components/scene-editors/sceneRailIcons';
import { EnvironmentsIcon, SelectedIcon, TexturesIcon } from '../../../../widgets/icons/scene';

interface MeshTreeViewItem {
    id: string;
    name: string;
}

enum Mode {
    Meshes = 'meshes',
    Materials = 'materials',
    Lights = 'lights',
    Camera = 'camera'
}

interface SceneEditorPageState {
    isEnvironmentTextureUploading: boolean;
    isSkyboxTextureUploading: boolean;
    zkModel: any;
    zkScene: any;
    meshes: any[];
    materials: any[];
    lights: any[];
    cameraLocations: any[];
    tasks: any[];
    selectedMeshes: any[];
    selectedMaterials: any[];
    selectedLights: any[];
    selectedCameraLocations: any[];
    filters: {
        meshes: string;
        lights: string;
        materials: string;
    };
    mode: Mode;
    propertiesPanelOpen: boolean;
    loadingPercentage: number;
    texturesLoading: any[];
    showPreview: boolean;
    explodeMode: boolean;
    hasVTOReferenceModels: boolean;
    selectedVTOType: any;
    tryOnSettingsComponent: React.RefObject<any>;
    isAdmin: boolean;
    importDropdownMenuIsOpened: boolean;
    isUploadingDamMaterial: boolean;
    providersMapping: {
        list: Record<string, any>;
    };
    uploadedQueue: any[];
    groupMaterialsDisplayOrder: Map<string, number>;
}

const Container = styled(FullscreenLayout)`
    align-items: stretch;
    background: linear-gradient(rgb(0 0 0 / 6%), rgb(0 0 0 / 6%)), #fff;
    z-index: 30;

    & > * {
        width: 100%;
    }
`;

const MainContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
`;

const PreviewContainer = styled(Panel)`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    min-height: 0;
    padding: 0;
    background: transparent;
    border: 0;
    border-radius: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.borderRadius};
    box-shadow: none;
`;

// IL RAIL DELL'INSPECTOR STA IN COLONNA, come quello di sinistra: è la stessa domanda — dove
// sono, e come chiudo — e la stessa risposta. Orizzontale era un secondo modo di dirla: una riga
// in più da leggere prima della testata, e una simmetria rotta fra i due bordi della schermata.
// Nel primo attracco non c'è: lì World ed Effects sono voci del rail di sinistra, insieme alle
// liste, e la colonna non è ridotta a rail perché un rail non ce l'ha.
const InspectorPanel = styled(Panel)<{ $railed?: boolean }>`
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: ${(props) => (props.$railed ? 'row' : 'column')};
`;

// Chiusa, della colonna resta il solo rail: il contenuto non si ritaglia, esce — come nel
// pannello di sinistra, e per la stessa ragione (un contenuto stretto a 43px si ridisegna tutto
// per non essere visto).
const InspectorBody = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;

    &[hidden] {
        display: none;
    }
`;

// Il posto in cui la sezione d'ambiente viene traslocata quando l'Inspector la mostra.
const InspectorSectionSlot = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
    flex: 1;
`;

// Lo scorrimento non sta qui: ce l'ha il corpo di `InspectorShell`, sotto la sua testata, che
// deve restare ferma mentre le proprietà scorrono.
const InspectorContent = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;
    overflow: hidden;
`;

// IL POSTO DICE CHE È VUOTO invece di sparire. Stessa forma del mockup: un glifo, una riga di
// prosa, tutto attenuato — non è un errore e non è un comando: è la colonna che aspetta un
// soggetto.
const InspectorEmpty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 32px 28px;
    text-align: center;
    color: ${(props) => props.theme.app.primary};

    p {
        max-width: 34ch;
        margin: 0;
        font-size: 13px;
        line-height: 1.6;
    }

    svg {
        width: 26px;
        height: 26px;
        opacity: 0.5;
    }
`;

const SmallScreenBanner = styled.div`
    display: none;

    @media (max-width: 940px) {
        display: block;
        position: absolute;
        top: 12px;
        left: 12px;
        right: 12px;
        z-index: 20;
    }
`;

const StageEmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.app.primary};
`;

const SceneEditor: React.FC = () => {
    // Esc chiude la finestra in primo piano, finché si lavora qui: vedi `useEscapeToClose`.
    useEscapeClosesModals();
    const { showDialog, closeDialog } = useDialogManager();
    // Hook per ottenere i dati della subscription con additionalFeatures
    const { subscription } = useSubscriptionData();
    const additionalFeatures = subscription?.plan?.additionalFeatures;

    // Refs per mantenere le referenze come nella classe
    // UN RIFERIMENTO PER SEZIONE, e non uno solo per tutte.
    //
    // «Portami a quella cosa» ha un bersaglio diverso a seconda di dove si va: una mesh
    // nell'albero degli oggetti, un materiale nella sua libreria. Con un riferimento unico vince
    // l'ultima sezione che si è montata — e siccome la libreria vive in un portale che sta in piedi
    // anche quando non la si guarda, vinceva lei: selezionare una mesh nel palco chiedeva a un
    // materiale di aprirsi, non lo trovava, e non apriva niente.
    //
    // Chi chiama dice già a quale sezione va (`revealTarget(tool, id)`): basta che il riferimento
    // lo scelga in base a quello.
    const treeRef = useRef<TreeHandle>(null);
    const materialsRef = useRef<TreeHandle>(null);
    const meshesScrollbarsRef = useRef<any>(null);
    const materialsScrollbarsRef = useRef<any>(null);
    const workspaceRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    // Le larghezze si salvano in frazioni del workspace: per convertirle serve la sua misura.
    const [measureWorkspace, workspaceSize] = useElementSize(workspaceRef);
    const { preferences, limits, defaults, setPreference, setPanelSize, resetPanelSizes, toggleCollapsed, snapshotPreferences, restorePreferences } =
        useWorkspacePreferences(workspaceSize);
    // L'ALONE È UNA PREFERENZA COME IL TEMA, e sta nello stesso documento sul server: il motore lo
    // vuole come oggetto, il documento lo tiene come due voci di testo.
    const selectionHighlight = useMemo<SelectionHighlight>(
        () => ({ enabled: preferences.selectionHighlight === 'on', color: preferences.highlightColor }),
        [preferences.highlightColor, preferences.selectionHighlight]
    );

    const updateSelectionHighlight = useCallback(
        (changes: Partial<SelectionHighlight>) => {
            if (changes.enabled !== undefined) setPreference('selectionHighlight', changes.enabled ? 'on' : 'off');
            if (changes.color !== undefined) setPreference('highlightColor', changes.color);
        },
        [setPreference]
    );
    const [inspectorContent, setInspectorContent] = useState<React.ReactNode | null>(null);
    const [libraryOpen, setLibraryOpen] = useState(true);

    // Hook per il salvataggio del prodotto e scena
    const [saveProductAndScene, isSaving] = useSaveProductAndScene();

    // Variabili di istanza convertite in useRef per persistenza
    const lastTasksLengthRef = useRef<number>(0);
    const treeItemsRef = useRef<MeshTreeViewItem[]>([]);
    const creatingGroupParentRef = useRef<any | null>(null);
    const sidebarWidthRef = useRef<number>(0);
    const sceneReadyRef = useRef<boolean>(false);
    const currentTaskRef = useRef<any | null>(null);

    // Stato iniziale convertito da this.state
    const [state, setState] = useState<SceneEditorPageState>({
        isEnvironmentTextureUploading: false,
        isSkyboxTextureUploading: false,
        zkModel: null,
        zkScene: undefined,
        meshes: [],
        materials: [],
        lights: [],
        cameraLocations: [],
        tasks: [],
        selectedMeshes: [],
        selectedMaterials: [],
        selectedLights: [],
        selectedCameraLocations: [],
        filters: { meshes: '', lights: '', materials: '' },
        mode: Mode.Meshes,
        propertiesPanelOpen: false,
        loadingPercentage: 0,
        texturesLoading: [],
        showPreview: false,
        explodeMode: false,
        hasVTOReferenceModels: false,
        selectedVTOType: undefined,
        tryOnSettingsComponent: createRef(),
        isAdmin: localStorage.getItem('is_admin') ? true : false,
        importDropdownMenuIsOpened: false,
        isUploadingDamMaterial: false,
        providersMapping: { list: {} },
        uploadedQueue: [],
        groupMaterialsDisplayOrder: new Map<string, number>()
    });

    // Stato per overlay loading (convertito da proprietà di classe)
    const { dispatch, state: productState, unsavedChanges } = useProductConfigurationState();
    const [zkProduct, zkScene, sceneEditor, activeTool, setActiveTool] = usePreview3DStore((x) => [
        x.zkProduct,
        x.zkScene,
        x.sceneEditor,
        x.sceneEditorActiveTool,
        x.setSceneEditorActiveTool
    ]);
    const [isSceneEditorReady, setIsSceneEditorReady] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    // IL FONDALE È UN MODO DI GUARDARE, e il suo interruttore sta sul palco — «Preview mode», in basso
    // a destra. Lo stato però vive qui e non nella barra, perché non ha un padrone solo: anche la
    // sezione World lo accende entrando, e uno stato tenuto dalla barra la sezione non saprebbe
    // raggiungerlo.
    const [backgroundOn, toggleBackground] = useSceneBackground(sceneEditor, selectionHighlight);

    const markSceneDirty = useCallback(() => {
        if (sceneReadyRef.current) setHasUnsavedChanges(true);
    }, []);

    // CERCARE NON È MODIFICARE. Le barre di ricerca sono campi come gli altri e stanno dentro
    // questo ascolto: senza l'eccezione, filtrare una lista o cercare un parametro lasciava la
    // scena «da salvare», e un avviso di modifiche non salvate che compare quando non si è
    // toccato niente smette di voler dire qualcosa. Chi cambia solo CIÒ CHE SI VEDE lo dichiara
    // con `data-view-only`, e lo dichiara chi lo sa: il campo stesso.
    const sceneMayHaveChanged = useCallback(
        (event: React.SyntheticEvent) => {
            if ((event.target as HTMLElement)?.closest?.('[data-view-only]')) return;
            markSceneDirty();
        },
        [markSceneDirty]
    );

    const activeSceneEditorTool = activeTool ?? T._('Objects', 'Admin');

    useEffect(() => {
        if (!activeTool) setActiveTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, activeTool, setActiveTool]);

    // Ref per la funzione di refresh della visibilità delle mesh
    const refreshMeshVisibilityRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        if (sceneEditor && zkScene) {
            sceneReadyRef.current = false;
            const meshes = sceneEditor.getSceneMeshes();
            const materials = sceneEditor.getAllMaterials();
            const lights = sceneEditor.getAllLights();

            // AGGIUNTO: Carica le camera locations dal zkScene
            const cameraLocations = zkScene.get('cameraLocations')?.models || [];

            handleMeshesUpdated(meshes);
            handleMaterialsUpdated(materials);
            handleCameraLocationsUpdated(cameraLocations);
            setHasUnsavedChanges(false);
            sceneReadyRef.current = true;
            setIsSceneEditorReady(true);

            // Refresh mesh visibility dopo il caricamento iniziale
            setTimeout(() => {
                if (refreshMeshVisibilityRef.current) {
                    refreshMeshVisibilityRef.current();
                }
            }, 100);
        }
        return () => {
            sceneReadyRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sceneEditor, zkScene]);

    // Event handlers stabilizzati con useCallback
    const handleMeshesUpdated = useCallback(
        (meshes: any[]) => {
            markSceneDirty();

            setState((prevState) => ({
                ...prevState,
                meshes: (meshes ?? [])
                    .filter((mesh) => {
                        return mesh.name.toLowerCase() !== 'material_mesh';
                    })
                    .sort((a: any, b: any) => {
                        if (a.name < b.name) return -1;
                        if (a.name > b.name) return 1;
                        return 0;
                    })
            }));
        },
        [markSceneDirty]
    );

    const handleMaterialsUpdated = useCallback(
        (materials: any[]) => {
            markSceneDirty();
            // Il problema: a volte riceviamo direttamente i materiali, altre volte un gruppo che li contiene
            let actualMaterials = materials;

            // Se il primo elemento ha una proprietà 'materials', allora è un gruppo
            if (materials && materials.length > 0 && materials[0] && materials[0].materials) {
                actualMaterials = materials[0].materials;
            }

            const filteredMaterials = (actualMaterials ?? [])
                .filter((material) => {
                    return material.name && material.name.toLowerCase() !== 'default_material';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

            setState((prevState) => ({
                ...prevState,
                materials: filteredMaterials
            }));
        },
        [markSceneDirty]
    );

    // Aggiorna handleLightsUpdated per essere più completo
    const handleLightsUpdated = useCallback(
        (lights: any[]) => {
            markSceneDirty();
            const filteredLights = (lights ?? [])
                .filter((light) => {
                    // Filtra eventuali luci di sistema o default se necessario
                    return light.name && light.name.toLowerCase() !== 'default_light';
                })
                .sort((a: any, b: any) => {
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });

            setState((prevState) => ({
                ...prevState,
                lights: filteredLights
            }));
        },
        [markSceneDirty]
    );

    const handleCameraLocationsUpdated = useCallback(
        (cameraLocations: any[]) => {
            markSceneDirty();
            setState((s) => ({ ...s, cameraLocations }));
        },
        [markSceneDirty]
    );

    // Callback per registrare la funzione di refresh delle mesh
    const handleRefreshMeshVisibility = useCallback((refreshFn: () => void) => {
        refreshMeshVisibilityRef.current = refreshFn;
    }, []);

    const handleSceneLoaded = useCallback((scene: BABYLON.Scene) => {
        void scene;
    }, []);

    const handleBeforeSceneLoaded = useCallback(async () => {
        return;
    }, []);

    const handleSceneTasksCheck = useCallback((...args: any[]) => {
        // If the first argument is the tasks array, update state accordingly
        const tasks = Array.isArray(args[0]) ? args[0] : args;
        setState((s) => ({ ...s, tasks }));
    }, []);

    const handleMeshUpdated = useCallback(
        (updatedMesh: any) => {
            markSceneDirty();
            setState((prevState) => ({
                ...prevState,
                meshes: prevState.meshes.map((mesh) => (mesh.uniqueId === updatedMesh.uniqueId ? updatedMesh : mesh))
            }));
        },
        [markSceneDirty]
    );

    const notifySceneEditorUpdates = useCallback(() => {
        if (!sceneEditor) return;

        sceneEditor.render();
        sceneEditor.updateBoundingBox();
    }, [sceneEditor]);

    // Funzione per il salvataggio usando l'hook
    const handleSave = useCallback(async () => {
        try {
            const productInfo = {
                name: productState.product.name,
                code: productState.product.code,
                environment: 2,
                // UN PRODOTTO DA ZERO VA SUL CANALE SCELTO NEL WIZARD, quello senza store: senza, il
                // salvataggio lo metterebbe sul canale 1. Quello dal catalogo resta dov'era.
                salesChannelID: productState.merchantProduct ? undefined : productState.salesChannelID || undefined
            };

            // ✅ NUOVO: Validazione AR PRIMA della notificazione di saving
            if (sceneEditor) {
                const { validateARBeforeSave } = await import('src/components/widgets/preview3D/hooks/useSaveProduct');
                await validateARBeforeSave(sceneEditor);
            }

            // Se il VTO è attivo, gestisci le impostazioni VTO prima del salvataggio
            if (sceneEditor && zkScene) {
                // Always hide all references on save so they are not visible and not
                // downloaded at start of customizer/DAM
                sceneEditor.setVTOReferenceVisible(false);

                // Verifica se il VTO è abilitato e applica le impostazioni
                const tryOnSettings = sceneEditor.getTryOnSettings();
                if (tryOnSettings && zkScene.getTryOnEnabled()) {
                    // Applica le impostazioni VTO correnti alla scena
                    sceneEditor.setTryOnTypeSettings(tryOnSettings);
                }

                // Imposta il nome della scena basato sul nome del prodotto
                zkScene.set('name', productInfo.name + ' Scene');
            }

            // Mostra notifica di progresso
            const notificationId = showInfoNotification({
                message: T._('Saving scene...', 'Admin')
            });

            // AL SALVATAGGIO SI RIEMPIONO I BUCHI. Un materiale senza fotografia resta un tondo
            // disegnato in libreria, e chi lo guarda non ha modo di sapere che basta chiederla: qui
            // si è già fermi ad aspettare, ed è il momento in cui uno scatto costa meno.
            //
            // SOLO QUELLE CHE MANCANO. Rifarle tutte vorrebbe dire minuti di scatti a ogni
            // salvataggio, e le altre sono già giuste. Le fotografie entrano nei metadata della
            // scena, quindi vanno prese PRIMA che la scena parta: dopo, sarebbero salvate la volta
            // dopo.
            // UN MATERIALE, UN FLUSSO. Chi ha provato tutte e due le sponde della finitura lascia sul
            // materiale quella scelta per ultima: le mappe dell'altra escono dagli slot e il core le
            // pota. Va fatto PRIMA delle fotografie, o lo scatto ritrarrebbe un materiale che non è
            // quello che si salva.
            commitFinishChoices(sceneEditor?.getAllMaterials?.() ?? []);

            const missingPreviews = materialsWithoutPreview(sceneEditor, state.materials);
            if (missingPreviews.length) await shootPreviews(sceneEditor, missingPreviews);

            await saveProductAndScene({
                onProgress: (progress) => {
                    void progress;
                },
                fromSceneEditor: true,
                // ✅ NUOVO: Passa i dati corretti del prodotto
                productInfo: productInfo
            });

            // IL SALVATAGGIO PUÒ SPEGNERE L'ALONE: per serializzare la scena il core butta lo strato
            // e lo ricrea vuoto (`getSceneJson`). Chi ha una mesh scelta la ritroverebbe senza.
            applySelectionHighlight(sceneEditor, selectionHighlight);

            // Rimuovi notifica di progresso e mostra successo
            removeNotification(notificationId);
            setHasUnsavedChanges(false);
            unsavedChanges.resetSnapshot();
            showSuccessNotification({
                message: T._('Scene saved successfully', 'Admin')
            });
            return true;
        } catch (error) {
            console.error('Errore durante il salvataggio:', error);

            // Mostra notifica di errore
            showErrorNotification({
                message: T._('Failed to save the scene', 'Admin')
            });
            return false;
        }
    }, [saveProductAndScene, sceneEditor, selectionHighlight, zkScene, productState.product, state.materials, unsavedChanges]);

    // LE DUE LISTE SI SEGUONO. Ritrovare una cosa vuol dire due movimenti insieme: la colonna di
    // sinistra cambia sezione, e dentro quella sezione la riga giusta si accende — coi gruppi
    // chiusi aperti lungo la strada. Prima il primo movimento mangiava il secondo: cliccando una
    // mesh nel palco mentre eri sui materiali, la sezione passava agli oggetti e lì finiva, con
    // l'elenco aperto sulla riga sbagliata. Bisognava cliccare la stessa mesh una seconda volta.
    //
    // IL PANNELLO NUOVO NON C'È ANCORA quando si chiede il cambio: si monta in questo giro di
    // render, e il suo albero esiste dal giro dopo. Si dichiara quindi cosa si stava cercando, e
    // appena la sezione è quella giusta la riga si accende da sé.
    const [pendingReveal, setPendingReveal] = useState<{ tool: string; dataID: string } | null>(null);

    const refOfTool = useCallback((tool: string) => (tool === T._('Materials', 'Admin') ? materialsRef : treeRef), []);

    const revealTarget = useCallback(
        (tool: string, dataID: string) => {
            if (activeSceneEditorTool !== tool) {
                setActiveTool(tool);

                // NON SI SVUOTA NELL'ATTESA. Il soggetto nuovo arriva un giro dopo, quando l'albero
                // della sezione si è montato: svuotare adesso fa chiudere e riaprire la colonna per una
                // cosa che sta per essere sostituita.
                setPendingReveal({ tool, dataID });
                return;
            }

            refOfTool(tool).current?.selectAndOpenOverlay?.(dataID);
        },
        [activeSceneEditorTool, setActiveTool, refOfTool]
    );

    useEffect(() => {
        if (!pendingReveal || activeSceneEditorTool !== pendingReveal.tool) return;

        const { dataID, tool } = pendingReveal;
        setPendingReveal(null);
        // Un giro dopo: l'albero della sezione nuova si sta montando proprio adesso.
        const frame = requestAnimationFrame(() => refOfTool(tool).current?.selectAndOpenOverlay?.(dataID));
        return () => cancelAnimationFrame(frame);
    }, [pendingReveal, activeSceneEditorTool, refOfTool]);

    const handleMeshSelected = useCallback((meshID: any) => revealTarget(T._('Objects', 'Admin'), meshID), [revealTarget]);

    // «DOVE STA?» NON È «PORTAMI LÀ», e finora erano la stessa funzione. Cliccare una mesh nel palco
    // vuol dire «adesso guardo questa», e sceglierla è la risposta giusta. La freccetta dentro uno
    // Slot chiede un'altra cosa: QUALE sia e DOVE stia — e rispondere scegliendola butta via il
    // soggetto che si stava leggendo, aprendo il pannello del materiale proprio a chi stava
    // guardando l'oggetto che lo usa.
    //
    // La domanda quindi cambia sezione e ACCENDE la riga, e basta: è la stessa risposta che l'albero
    // dà già alla freccetta della sua testata.
    const [whereRequest, setWhereRequest] = useState<{ tool: string; dataID: string; token: number } | null>(null);

    const askWhereItIs = useCallback(
        (tool: string, dataID: string) => {
            if (activeSceneEditorTool !== tool) setActiveTool(tool);

            // IL GETTONE SERVE A CHIEDERE DUE VOLTE LA STESSA RIGA: senza, la seconda domanda sarebbe
            // lo stesso valore di prima e non succederebbe niente.
            setWhereRequest((previous) => ({ tool, dataID, token: (previous?.token ?? 0) + 1 }));
        },
        [activeSceneEditorTool, setActiveTool]
    );

    // I DUE «PORTAMI LÀ» SONO FUNZIONI STABILI, e non è pignoleria: scritti in linea dentro il JSX
    // nascono nuovi a ogni ridisegno di questa pagina, e una sezione che li mette fra le dipendenze
    // di una `useCallback` si ritrova a rifare tutto — la fabbrica dei suoi pannelli, il suo albero
    // di nodi — a ogni giro. Costa poco scriverli qui e vale per chiunque li riceva: è l'invariante
    // che rende innocuo dipenderne.
    const revealMaterial = useCallback((materialID: string) => askWhereItIs(T._('Materials', 'Admin'), materialID), [askWhereItIs]);
    const revealObject = useCallback((meshID: string) => askWhereItIs(T._('Objects', 'Admin'), meshID), [askWhereItIs]);

    // I DATI VIVI PASSANO DA UN RIFERIMENTO, e non dalla chiusura delle fabbriche qui sotto: è la
    // condizione che permette a quelle di avere identità fissa. Si riempie durante il disegno,
    // subito prima che le sezioni lo leggano.
    const live = useRef<any>({});

    live.current = {
        state,
        sceneEditor,
        zkScene,
        additionalFeatures,
        handleMeshUpdated,
        notifySceneEditorUpdates,
        handleMeshesUpdated,
        handleMaterialsUpdated,
        handleLightsUpdated,
        handleCameraLocationsUpdated,
        handleRefreshMeshVisibility,
        revealMaterial,
        revealObject,
        whereRequest,
        markSceneDirty
    };

    // LE FABBRICHE DELLE SEZIONI HANNO IDENTITÀ FISSA, e non è un'ottimizzazione. La barra a
    // sinistra rende `component` come TIPO di componente — `<ToolComponent/>` — e per React un tipo
    // diverso è un componente diverso: smonta quello di prima e ne monta uno nuovo da zero. Scritte
    // in linea dentro il memo qui sotto, queste funzioni rinascevano a ogni cambio di `state.lights`
    // o `state.materials`, cioè a ogni modifica della scena.
    //
    // COSA COSTAVA: la sezione aperta perdeva tutto — il suo elenco, la selezione, la riga aperta —
    // e siccome nessuno aveva più una riga aperta, nessuno rifaceva il pannello: la colonna di
    // destra restava a disegnare l'ultima cosa che le era stata consegnata. Da fuori era «rinomino
    // una luce e il pannello mostra il nome vecchio». Il cambio di specie non si riprendeva mai,
    // perché a morire era anche il ricordo della luce appena nata che serviva a riaprirla.
    const ObjectsTool = useCallback(
        (props: any) => (
            <ObjectEditor
                onMeshUpdate={live.current.handleMeshUpdated}
                sceneEditor={live.current.sceneEditor}
                notifySceneEditorUpdates={live.current.notifySceneEditorUpdates}
                onMeshesUpdated={live.current.handleMeshesUpdated}
                onRevealMaterial={live.current.revealMaterial}
                onRefreshMeshVisibility={live.current.handleRefreshMeshVisibility}
                whereRequest={live.current.whereRequest?.tool === T._('Objects', 'Admin') ? live.current.whereRequest : null}
                {...props}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const LightsTool = useCallback(
        (props: any) => (
            <LightsEditor
                {...props}
                lights={live.current.state.lights}
                selectedLights={live.current.state.selectedLights}
                sceneEditor={live.current.sceneEditor}
                onLightsUpdated={live.current.handleLightsUpdated}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const CamerasTool = useCallback(
        (props: any) => (
            <CameraEditor
                {...props}
                cameraLocations={live.current.state.cameraLocations}
                selectedCameraLocations={live.current.state.selectedCameraLocations}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                onCameraLocationsUpdated={live.current.handleCameraLocationsUpdated}
                treeRef={treeRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const MaterialsTool = useCallback(
        (props: any) => (
            <MaterialsEditor
                {...props}
                materials={live.current.state.materials}
                selectedMaterials={live.current.state.selectedMaterials}
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                onMaterialsUpdated={live.current.handleMaterialsUpdated}
                onRevealObject={live.current.revealObject}
                onSceneChanged={live.current.markSceneDirty}
                whereRequest={live.current.whereRequest?.tool === T._('Materials', 'Admin') ? live.current.whereRequest : null}
                treeRef={materialsRef}
                additionalFeatures={live.current.additionalFeatures}
            />
        ),
        []
    );

    const TexturesTool = useCallback(() => <TexturesEditor />, []);

    const EnvironmentsTool = useCallback(() => <EnvironmentsEditor />, []);

    const WorldTool = useCallback(
        () => (
            <WorldEditor
                sceneEditor={live.current.sceneEditor}
                zkScene={live.current.zkScene}
                additionalFeatures={live.current.additionalFeatures}
                onSceneChanged={() => live.current.markSceneDirty()}
            />
        ),
        []
    );

    const EffectsTool = useCallback(() => <EffectsEditor sceneEditor={live.current.sceneEditor} zkScene={live.current.zkScene} />, []);

    // NOVE VOCI IN TRE GENERI, come il prototipo. Sopra ciò che sta NELLA scena — oggetti,
    // luci, camere, trigger point; in mezzo la LIBRERIA da cui la scena pesca — materiali,
    // texture, ambienti; sotto ciò che sta INTORNO — il mondo e gli effetti. I separatori
    // sono i capitoli: senza, nove glifi in colonna sono un elenco senza indice.
    const declaredSceneEditorTools: StepsTools[] = useMemo(
        () => [
            {
                tool: T._('Objects', 'Admin'),
                icon: <ObjectsIcon />,
                tooltip: T._('Objects', 'Admin'),
                onClick: () => {},
                component: ObjectsTool
            },
            {
                tool: T._('Lights', 'Admin'),
                icon: <LightsIcon />,
                tooltip: T._('Lights', 'Admin'),
                onClick: () => {},
                component: LightsTool
            },
            {
                tool: T._('Cameras', 'Admin'),
                icon: <CamerasIcon />,
                tooltip: T._('Cameras', 'Admin'),
                onClick: () => {},
                component: CamerasTool
            },
            {
                tool: T._('Materials', 'Admin'),
                icon: <MaterialsIcon />,
                tooltip: T._('Materials', 'Admin'),
                onClick: () => {},
                separatorBefore: true,
                component: MaterialsTool
            },
            {
                tool: T._('Textures', 'Admin'),
                icon: <TexturesIcon />,
                tooltip: T._('Textures', 'Admin'),
                onClick: () => {},
                component: TexturesTool
            },
            {
                tool: T._('Env', 'Admin'),
                icon: <EnvironmentsIcon />,
                tooltip: T._('Environments', 'Admin'),
                onClick: () => {},
                component: EnvironmentsTool
            },
            {
                tool: T._('World', 'Admin'),
                icon: <WorldIcon />,
                tooltip: T._('World', 'Admin'),
                onClick: () => {},
                separatorBefore: true,
                component: WorldTool
            },
            {
                tool: T._('Effects', 'Admin'),
                icon: <EffectsIcon />,
                tooltip: T._('Effects', 'Admin'),
                onClick: () => {},
                component: EffectsTool
            }
        ],
        [ObjectsTool, LightsTool, CamerasTool, MaterialsTool, TexturesTool, EnvironmentsTool, WorldTool, EffectsTool]
    );

    // LE SEZIONI ANCORA VUOTE: trigger point, texture e ambienti hanno un editor che è solo un
    // guscio, perché i dati dietro non esistono ancora (docs/scene-editor-new-sections.md). Le voci
    // restano dichiarate qui sopra e, finché `SHOW_NOT_YET_AVAILABLE` è spento, non si mostrano —
    // né nel rail né nella striscia della libreria. Una sezione che si riempie esce da questo
    // elenco, e riprende posto, glifo e ordine di prima.
    const deferredToolNames = useMemo<string[]>(() => (SHOW_NOT_YET_AVAILABLE ? [] : [T._('Textures', 'Admin'), T._('Env', 'Admin')]), []);

    const sceneEditorTools = useMemo(
        () => declaredSceneEditorTools.filter((stepTool) => !deferredToolNames.includes(stepTool.tool)),
        [declaredSceneEditorTools, deferredToolNames]
    );

    // Chiudere il pannello lo chiude per tutte: non si sa da quale sezione era stato aperto, e una
    // sezione che resta convinta di avere un pannello aperto lo riapre al primo aggiornamento.
    const closeOverlay = useCallback(() => {
        setInspectorContent(null);
        treeRef.current?.closeOverlay();
        materialsRef.current?.closeOverlay();
    }, []);

    // Lo store del motore, non il suo contenuto: serve a riportarlo dentro le finestre, che si
    // disegnano fuori da questo albero di componenti.
    const preview3DStore = useContext(Preview3DStoreContext);

    const openSettings = useCallback(() => {
        // Le impostazioni si applicano mentre le tocchi — un tema lo si giudica vedendolo —
        // ma «Cancel» le rimette com'erano davvero: la revoca la scrive chi apre, che è
        // l'unico a sapere cosa c'era prima.
        const before = snapshotPreferences();

        showDialog(
            'scene-editor-settings',
            // LA FINESTRA ESCE DAL PANNELLO, e con lui dal contesto del motore 3D: le finestre di questo
            // backoffice si disegnano in un portale attaccato alla radice della pagina, quindi tutto ciò
            // che sta dentro si ritrova FUORI dal provider che tiene scena, editor e prodotto. Il VTO lo
            // chiede al primo respiro e cadeva con «usePreview3DStore must be used within a
            // Preview3DStoreContext.Provider» — cioè la pagina intera andava in errore aprendo una
            // sezione delle impostazioni.
            //
            // Si riporta dentro lo stesso store, non una copia: è il gesto che il DAM e il caricamento
            // dei materiali fanno già quando aprono una finestra loro.
            <Preview3DStoreContext.Provider value={preview3DStore}>
                <Dialog
                    title={T._('Settings', 'Admin')}
                    size='large'
                    onClose={() => {
                        restorePreferences(before);
                        closeDialog('scene-editor-settings');
                    }}
                    buttons={[
                        {
                            label: T._('Cancel', 'Admin'),
                            outline: true,
                            onClick: () => {
                                restorePreferences(before);
                                closeDialog('scene-editor-settings');
                            }
                        },
                        { label: T._('Confirm', 'Admin'), onClick: () => closeDialog('scene-editor-settings') }
                    ]}
                >
                    <SceneEditorSettingsDialog
                        host='sceneEditor'
                        preferences={preferences}
                        setPreference={setPreference}
                        selectionHighlight={selectionHighlight}
                        onSelectionHighlightChange={updateSelectionHighlight}
                        onResetPanelSizes={resetPanelSizes}
                        sceneEditor={sceneEditor}
                        zkScene={zkScene}
                        zkProduct={zkProduct}
                        additionalFeatures={additionalFeatures}
                        onSceneChanged={markSceneDirty}
                    />
                </Dialog>
            </Preview3DStoreContext.Provider>
        );
    }, [
        additionalFeatures,
        closeDialog,
        markSceneDirty,
        preferences,
        preview3DStore,
        resetPanelSizes,
        restorePreferences,
        sceneEditor,
        selectionHighlight,
        setPreference,
        showDialog,
        snapshotPreferences,
        updateSelectionHighlight,
        zkProduct,
        zkScene
    ]);

    const showCentralLibrary = preferences.layoutMode === 'workspace' && preferences.propertiesPanel === 'right';
    // Con la striscia sotto il palco la libreria ha già il suo posto: tenere le sue
    // categorie anche nel rail vorrebbe dire due porte sulle stesse sezioni. Nell'altro
    // attracco la striscia non esiste, e tornano liste come le altre. Quelle rimandate al
    // secondo round mancano anche qui: una linguetta spenta è una sezione vuota con un altro vestito.
    const libraryToolNames = useMemo(
        () => [T._('Materials', 'Admin'), T._('Textures', 'Admin'), T._('Env', 'Admin')].filter((tool) => !deferredToolNames.includes(tool)),
        [deferredToolNames]
    );
    // World ed Effects non parlano di una cosa scelta, parlano della scena: nel secondo
    // attracco sono voci del rail dell'**Inspector**, dove stanno accanto a «Selected» — cioè
    // accanto all'altra risposta alla stessa domanda, «cosa sto guardando».
    const ambientToolNames = useMemo(() => [T._('World', 'Admin'), T._('Effects', 'Admin')], []);
    const relocatedToolNames = useMemo(() => [...libraryToolNames, ...ambientToolNames], [libraryToolNames, ambientToolNames]);

    const railTools = useMemo(() => {
        if (!showCentralLibrary) return sceneEditorTools;
        return sceneEditorTools.filter((stepTool) => !relocatedToolNames.includes(stepTool.tool));
    }, [sceneEditorTools, showCentralLibrary, relocatedToolNames]);

    // LA SEZIONE DELLA LIBRERIA È UNA SOLA, E CAMBIA CASA. Nella colonna sta nell'Outliner,
    // nella striscia sta sotto il palco — ma è **lo stesso nodo**, non due copie: due
    // implementazioni sarebbero due stati da tenere d'accordo, ed è esattamente il difetto
    // che c'era (la striscia disegnava tessere finte e il layout 2 perdeva la sezione).
    //
    // In React spostare un componente da un genitore all'altro lo smonta e lo rimonta:
    // selezione, filtro e anteprime se ne andrebbero a ogni cambio d'attracco. Quindi il
    // nodo che lo contiene si crea **una volta**, la sezione ci vive dentro per portale, e a
    // spostarsi è il nodo — con `appendChild`, e **solo se il genitore è cambiato davvero**,
    // perché rimetterlo a ogni giro gli farebbe perdere il fuoco e azzererebbe lo
    // scorrimento (docs/scene-editor-step6-second-layout.md § 1).
    const [libraryTool, setLibraryTool] = useState(() => libraryToolNames[0]);
    const activeLibraryTool = libraryToolNames.includes(activeSceneEditorTool) ? activeSceneEditorTool : libraryTool;
    const librarySection = useMemo(() => sceneEditorTools.find((stepTool) => stepTool.tool === activeLibraryTool), [sceneEditorTools, activeLibraryTool]);

    useEffect(() => {
        if (libraryToolNames.includes(activeSceneEditorTool)) setLibraryTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, libraryToolNames]);

    // Le linguette della striscia sono le stesse voci del rail: stesso nome, stesso glifo,
    // stesso ordine. Il conto lo porta chi ce l'ha, e una voce che non ha ancora niente dietro
    // resta spenta.
    const libraryCategories = useMemo(
        () =>
            libraryToolNames.map((tool) => {
                const stepTool = sceneEditorTools.find((candidate) => candidate.tool === tool);
                const isMaterials = tool === libraryToolNames[0];
                return {
                    tool,
                    icon: stepTool?.icon,
                    count: isMaterials ? state.materials.length : 0,
                    disabled: !isMaterials
                };
            }),
        [libraryToolNames, sceneEditorTools, state.materials.length]
    );

    // SI RIDUCE A RAIL SOLO CHI UN RAIL CE L'HA. La colonna dell'Inspector si stringe in una
    // striscia perché quella striscia è il suo rail: nel secondo attracco ci restano dentro
    // «Selected», World ed Effects, cioè un modo di tornare. Nel primo attracco quel rail non
    // esiste, e una striscia con dentro solo una freccetta è spazio occupato per non dire
    // niente — lì l'Inspector se ne va del tutto, com'è giusto: la colonna è il soggetto, e
    // senza soggetto non c'è colonna.
    const isInspectorCollapsible = showCentralLibrary;

    // I comandi della colonna, non del soggetto: li prende il guscio dell'Inspector.
    //
    // DOVE C'È IL RAIL LA TESTATA NON PORTA NIENTE: chiudere è un gesto della colonna, e la colonna
    // ha già il suo posto per i gesti — lo stesso che ha quella di sinistra. Una X in testata
    // sarebbe una seconda porta per la stessa stanza, e per giunta che sparisce quando la colonna è
    // chiusa. Nel primo attracco il rail non c'è e la X resta, ma vuol dire un'altra cosa: lì
    // l'Inspector non si riduce, si svuota.
    const inspectorChrome = useMemo(() => ({ onCollapse: isInspectorCollapsible ? undefined : closeOverlay }), [closeOverlay, isInspectorCollapsible]);

    // LA SEZIONE D'AMBIENTE, stessa storia della libreria: una sola, che cambia casa. Nel
    // primo attracco è una voce del rail come le altre, nel secondo è una voce del rail
    // dell'Inspector — e in mezzo non si rimonta.
    const [ambientTool, setAmbientTool] = useState(() => ambientToolNames[0]);
    const activeAmbientTool = ambientToolNames.includes(activeSceneEditorTool) ? activeSceneEditorTool : ambientTool;
    const ambientSection = useMemo(() => sceneEditorTools.find((stepTool) => stepTool.tool === activeAmbientTool), [sceneEditorTools, activeAmbientTool]);

    useEffect(() => {
        if (ambientToolNames.includes(activeSceneEditorTool)) setAmbientTool(activeSceneEditorTool);
    }, [activeSceneEditorTool, ambientToolNames]);

    // Cosa mostra l'Inspector nel secondo attracco: la cosa scelta, oppure una delle due
    // sezioni d'ambiente. Nel primo attracco la domanda non si pone — lì l'Inspector ha un
    // soggetto solo e le due sezioni stanno nel rail di sinistra.
    const [inspectorTool, setInspectorTool] = useState<'selected' | 'ambient'>('selected');

    // APRIRE WORLD ACCENDE IL FONDALE (utente, 2026-09-22). World governa environment, fondale e
    // pavimento — cioè le tre cose che nel modo Editor NON si disegnano: aprirlo senza accendere
    // vuol dire regolare a occhi chiusi, e girare una manopola che non muove niente è il modo più
    // rapido per crederla rotta.
    //
    // La sezione sta in due posti a seconda dell'attracco — nel rail dell'Inspector quando le
    // proprietà sono di fianco, fra le voci del rail di sinistra quando sono a destra — e quindi la
    // domanda «è a schermo?» ha due risposte, non una.
    //
    // SI ACCENDE ENTRANDO, NON SI SPEGNE USCENDO: chi lo spegne mentre è dentro World lo ha spento
    // apposta, e riaccenderlo al primo cambio di sezione sarebbe disfargli il gesto.
    const worldToolName = T._('World', 'Admin');
    const isWorldOnScreen = showCentralLibrary ? inspectorTool === 'ambient' && activeAmbientTool === worldToolName : activeSceneEditorTool === worldToolName;

    useEffect(() => {
        if (isWorldOnScreen && !backgroundOn) toggleBackground();
    }, [isWorldOnScreen, backgroundOn, toggleBackground]);

    const railContentRef = useRef<HTMLDivElement | null>(null);
    const stripBodyRef = useRef<HTMLDivElement>(null);
    const inspectorSlotRef = useRef<HTMLDivElement | null>(null);
    // La presa del secondo pannello si misura sul pannello stesso, non sul palco: vedi `side` in
    // `workspaceLayout`.
    const inspectorRef = useRef<HTMLDivElement | null>(null);
    const { hostRef: libraryHostRef, moveTo: moveLibrary } = useMovableHost();
    const { hostRef: ambientHostRef, moveTo: moveAmbient } = useMovableHost();

    const railSelectedTool = railTools.some((stepTool) => stepTool.tool === activeSceneEditorTool) ? activeSceneEditorTool : railTools[0]?.tool;
    const isLibraryExpanded = showCentralLibrary && libraryOpen && !preferences.collapsed.includes('assets');

    // Il trasloco. Nella colonna il nodo entra nel contenitore del rail, ma solo quando la
    // sezione scelta è una della libreria: se no lì c'è un'altra sezione e i due si
    // affiancherebbero. Nella striscia entra nel corpo, anche da chiusa — è il posto vuoto
    // che resta, ed è dove la sezione torna.
    useLayoutEffect(() => {
        moveLibrary(showCentralLibrary ? stripBodyRef.current : libraryToolNames.includes(activeSceneEditorTool) ? railContentRef.current : null);
    }, [moveLibrary, showCentralLibrary, activeSceneEditorTool, libraryToolNames, isLibraryExpanded, preferences.collapsed]);

    // NEL SECONDO ATTRACCO LA COLONNA C'È SEMPRE (collega, 2026-09-09: «il pannello destro non
    // chiuderlo mai: sostituisci il contenuto interno»). Lì l'Inspector ha un rail suo —
    // «Selected», World, Effects — quindi è una PARETE della stanza, non il soggetto: farla sparire
    // a ogni clic a vuoto rifaceva la griglia due volte e si portava via anche il rail, cioè il
    // modo di tornare. Quando nessuno è scelto la colonna resta, e lo dice.
    //
    // NEL PRIMO ATTRACCO resta com'era: lì la colonna È il soggetto, rail non ne ha, e senza
    // soggetto occuperebbe spazio per dire «scegli qualcosa».
    const showAmbientInInspector = showCentralLibrary && inspectorTool === 'ambient';
    const showInspectorPanel = preferences.layoutMode === 'workspace' && (showCentralLibrary || Boolean(inspectorContent));
    const isInspectorCollapsed = isInspectorCollapsible && preferences.collapsed.includes('right');
    const isInspectorOpen = showInspectorPanel && !isInspectorCollapsed;

    // La griglia deve sapere *che cosa è davvero ridotto a rail*, non cosa risulta scritto
    // nelle preferenze: un «right» rimasto lì da quando la colonna si riduceva stringerebbe
    // una traccia che ora non ha niente da metterci dentro.
    const collapsedPanels = preferences.collapsed.filter((panel) => panel !== 'right' || isInspectorCollapsed).join(' ') || undefined;

    useLayoutEffect(() => {
        moveAmbient(
            showCentralLibrary
                ? isInspectorOpen && showAmbientInInspector
                    ? inspectorSlotRef.current
                    : null
                : ambientToolNames.includes(activeSceneEditorTool)
                  ? railContentRef.current
                  : null
        );
    }, [moveAmbient, showCentralLibrary, showAmbientInInspector, isInspectorOpen, activeSceneEditorTool, ambientToolNames]);

    // Scegliere una cosa riporta l'Inspector sulla cosa: è la risposta al gesto che hai
    // appena fatto, e trovarci ancora World sarebbe un silenzio.
    //
    // E RIAPRE LA COLONNA, SE ERA CHIUSA. La chiusura vive nelle preferenze e sopravvive al
    // ricaricamento: senza questo, un solo clic sulla ✕ rendeva muto ogni clic successivo su una
    // riga — il soggetto cambiava davvero, ma la colonna restava una striscia e non lo diceva
    // nessuno. Chi sceglie una cosa sta chiedendo di vederla, e la richiesta batte una chiusura
    // fatta ieri su un altro soggetto: a tenere la colonna stretta resta il gesto di chiuderla,
    // che è lì e si rifà in un clic.
    //
    // LO STATO LO LEGGE UN RIFERIMENTO, non le dipendenze: con `preferences.collapsed` fra le
    // dipendenze, chiudere la colonna di SINISTRA rieseguirebbe questo effetto e riporterebbe
    // l'Inspector su «Selected» mentre stai guardando World.
    const revealInspector = useRef<() => void>(() => {});
    revealInspector.current = () => {
        setInspectorTool('selected');
        if (preferences.collapsed.includes('right')) toggleCollapsed('right');
    };

    useEffect(() => {
        if (inspectorContent) revealInspector.current();
    }, [inspectorContent]);
    const workspaceStyle = {
        '--w-left': `${preferences.sizes.left}px`,
        '--w-right': `${preferences.sizes.right}px`,
        '--h-assets': showCentralLibrary ? `${isLibraryExpanded ? preferences.sizes.assets : 43}px` : '0px',
        '--w-rail-cur': `${getWorkspaceRailWidth(preferences.sectionRails)}px`
    } as React.CSSProperties;

    return (
        // L'ATTESA È LA STESSA DEL COMPOSER, card compresa: sono due ambienti della stessa
        // piattaforma, e prima che compaiano non c'è altro da guardare che questa. Il velo nudo di
        // prima mostrava i pallini senza dire che cosa stesse caricando — su una scena 3D, che ci
        // mette secondi veri, si leggeva come una pagina piantata.
        <Load3DSceneEditor
            onProgress={() => null}
            fallback={
                <Container>
                    <EditorLoadingCard
                        title={T._('Loading Scene Editor', 'Admin')}
                        lines={[T._('We are loading the 3D scene and its resources. This may take a few moments.', 'Admin')]}
                    />
                </Container>
            }
            damMode={false}
            isConfiguratorProduct={true}
            notLoadDesign={true}
        >
            <Container>
                {/* Non disegna niente: dice al motore chi è il provider VTO di questo prodotto. */}
                <VTOProviderSync productModelID={zkProduct?.get?.('modelID')} />
                <HeaderToolbar
                    closeOverlay={closeOverlay}
                    hasUnsavedChanges={hasUnsavedChanges}
                    onSave={handleSave}
                    isSaving={isSaving}
                    onSettings={openSettings}
                    activeMode='scene'
                    /* Un prodotto nato da zero non ha ancora una fotografia da cui misurare le modifiche:
                       un nome nuovo lo si dice da qui. */
                    onProductRenamed={markSceneDirty}
                />
                {/* CHI ASCOLTA LE MODIFICHE STA SOPRA IL PALCO, non sul palco. Le sezioni che
                    traslocano — la libreria, gli ambienti — si vedono dentro il Workspace ma
                    nell'albero di React sono qui fuori, appese ai portali in fondo: un ascolto
                    messo sul Workspace non le sentiva, e una texture cambiata dalla libreria
                    lasciava la scena pulita quando invece era sporca. */}
                <MainContentContainer onChangeCapture={sceneMayHaveChanged} onInputCapture={sceneMayHaveChanged}>
                    <Workspace
                        ref={measureWorkspace}
                        className='workspace'
                        data-layout={preferences.layoutMode}
                        data-dock={preferences.propertiesPanel}
                        data-inspector={showInspectorPanel ? 'on' : 'off'}
                        data-collapse={collapsedPanels}
                        style={workspaceStyle}
                    >
                        <Panel data-panel='left'>
                            <LeftVerticalToolbar
                                tools={railTools}
                                selectedTool={railSelectedTool}
                                /* CAMBIARE SEZIONE NON CHIUDE IL PANNELLO. La colonna di destra mostra il
                                   SOGGETTO, e cambiare la lista da cui si sceglie non è dire che non si sta
                                   più guardando niente: era un pannello che si chiudeva da solo mentre
                                   navighi, portandosi via quello che stavi tenendo d'occhio.

                                   E siccome la colonna esce dalla griglia quando non ha contenuto, quel
                                   vuoto non era nemmeno un pannello vuoto: era la colonna che spariva e
                                   ricompariva, spostando tutto il resto due volte.

                                   Il soggetto lo cambia chi ne sceglie un altro, o chi chiude. */
                                onSelectTool={setActiveTool}
                                onClosePanel={() => toggleCollapsed('left')}
                                collapsed={preferences.collapsed.includes('left')}
                                sectionRails={preferences.sectionRails}
                                onInspectorChange={setInspectorContent}
                                hostedTools={relocatedToolNames}
                                contentRef={railContentRef}
                            />
                        </Panel>
                        {/* LA PRESA STA FUORI DAL PANNELLO, non dentro. Dentro veniva tagliata dal suo
                            `overflow: hidden` e si misurava sul workspace invece che sul pannello: c'era,
                            ma non si poteva né vedere né afferrare. Da qui si posiziona da sé sulla
                            cucitura fra la prima colonna e ciò che le sta accanto.
                            E non c'è quando il pannello è chiuso: un confine si tira solo se ha due lati
                            da spartire. */}
                        {!preferences.collapsed.includes('left') && (
                            <Resizer
                                direction='left'
                                value={preferences.sizes.left}
                                min={limits.left.min}
                                max={limits.left.max}
                                defaultValue={defaults.left}
                                workspaceRef={workspaceRef}
                                cssVariable='--w-left'
                                onCommit={(value) => setPanelSize('left', value)}
                            />
                        )}
                        <StageColumn ref={stageRef} data-panel='stage' $hasAssetBar={showCentralLibrary}>
                            <PreviewContainer>
                                <SmallScreenBanner>
                                    <NotificationBanner type='INFO' variant='slim' content={T._('The side panels are hidden on small screens.', 'Admin')} />
                                </SmallScreenBanner>
                                {zkScene && zkProduct && sceneEditor && (
                                    <>
                                        <SceneEditorView
                                            variantId={zkProduct.get('colors').at(0).get('colorID')}
                                            isHighlightActive={true}
                                            selectedMeshId={null}
                                            isSelectionMode={true}
                                            onSelectionChanged={handleMeshSelected}
                                            onMeshesUpdated={handleMeshesUpdated}
                                            onMaterialsUpdated={handleMaterialsUpdated}
                                            onLightsUpdated={handleLightsUpdated}
                                            onCameraLocationsUpdated={handleCameraLocationsUpdated}
                                            onSceneTasksCheck={handleSceneTasksCheck}
                                        />
                                        {isSceneEditorReady && (
                                            <SceneEditorToolbar
                                                sceneEditor={sceneEditor}
                                                stageRef={stageRef}
                                                previewActive={backgroundOn}
                                                onPreviewToggle={toggleBackground}
                                                measurementUnit={preferences.measurementUnit}
                                                onMeasurementUnitChange={(unit) => setPreference('measurementUnit', unit)}
                                                selectionHighlight={selectionHighlight}
                                            />
                                        )}
                                    </>
                                )}
                                {!sceneEditor && <StageEmptyState>{T._('Loading scene...', 'Admin')}</StageEmptyState>}
                            </PreviewContainer>
                            {showCentralLibrary && (
                                <AssetBar data-panel='assets' aria-label={T._('Asset library', 'Admin')}>
                                    <SceneEditorLibraryDrawer
                                        categories={libraryCategories}
                                        activeTool={activeLibraryTool}
                                        onSelectTool={setLibraryTool}
                                        isOpen={isLibraryExpanded}
                                        onToggle={() => {
                                            if (preferences.collapsed.includes('assets')) {
                                                toggleCollapsed('assets');
                                            } else {
                                                setLibraryOpen((current) => !current);
                                            }
                                        }}
                                        bodyRef={stripBodyRef}
                                    />
                                </AssetBar>
                            )}
                            {/* Il manico del divisore vive dove vive la colonna che allarga: senza
                                Inspector aperto tirerebbe una traccia che non c'è. */}
                            {preferences.propertiesPanel === 'right' && isInspectorOpen && (
                                <Resizer
                                    direction='right'
                                    value={preferences.sizes.right}
                                    min={limits.right.min}
                                    max={limits.right.max}
                                    defaultValue={defaults.right}
                                    workspaceRef={workspaceRef}
                                    referenceRef={stageRef}
                                    cssVariable='--w-right'
                                    onCommit={(value) => setPanelSize('right', value)}
                                />
                            )}
                            {showCentralLibrary && isLibraryExpanded && (
                                <Resizer
                                    direction='assets'
                                    value={preferences.sizes.assets}
                                    min={limits.assets.min}
                                    max={limits.assets.max}
                                    defaultValue={defaults.assets}
                                    workspaceRef={workspaceRef}
                                    cssVariable='--h-assets'
                                    onCommit={(value) => setPanelSize('assets', value)}
                                />
                            )}
                        </StageColumn>
                        {/* ANCHE IL SECONDO PANNELLO SI ALLARGA. Il primo aveva la sua presa dal principio, il
                            secondo no: nell'attracco `right` la cucitura è fra palco e Inspector e la presa vive
                            là, ma qui l'Inspector sta IN MEZZO — fra le sezioni e il palco — e quella cucitura non
                            aveva nessuno. Due colonne larghe uguali e nessun modo di dare aria a quella che in quel
                            momento stai leggendo. */}
                        {preferences.propertiesPanel === 'side' && isInspectorOpen && (
                            <Resizer
                                direction='side'
                                value={preferences.sizes.right}
                                min={limits.right.min}
                                max={limits.right.max}
                                defaultValue={defaults.right}
                                workspaceRef={workspaceRef}
                                referenceRef={inspectorRef}
                                cssVariable='--w-right'
                                onCommit={(value) => setPanelSize('right', value)}
                            />
                        )}
                        {showInspectorPanel && (
                            <InspectorPanel ref={inspectorRef} data-panel='right' $railed={showCentralLibrary}>
                                {/* La testata è del SOGGETTO, non della colonna: la disegna
                                    `InspectorShell` insieme a nome e specifiche. I comandi della
                                    colonna — chiuderla — li porta il rail dove il rail c'è, e il
                                    guscio non mette nessuna X: due modi di chiudere la stessa
                                    colonna sono uno di troppo. */}
                                <InspectorBody hidden={isInspectorCollapsed}>
                                    <InspectorChromeProvider value={inspectorChrome}>
                                        <InspectorContent>
                                            {showAmbientInInspector ? (
                                                <InspectorSectionSlot ref={inspectorSlotRef} />
                                            ) : (
                                                (inspectorContent ?? (
                                                    <InspectorEmpty>
                                                        <SelectedIcon aria-hidden='true' />
                                                        <p>{T._('Select something in a list or in the model to see its properties.', 'Admin')}</p>
                                                    </InspectorEmpty>
                                                ))
                                            )}
                                        </InspectorContent>
                                    </InspectorChromeProvider>
                                </InspectorBody>
                                {showCentralLibrary && (
                                    <SectionRail
                                        items={[
                                            { key: 'selected', icon: <SelectedIcon />, label: T._('Selected', 'Admin') },
                                            ...ambientToolNames.map((tool, index) => ({
                                                key: tool,
                                                icon: sceneEditorTools.find((stepTool) => stepTool.tool === tool)?.icon,
                                                label: tool,
                                                // «Selected» è ciò che stai guardando, World ed Effects ciò che gli sta intorno: due generi.
                                                separatorBefore: index === 0
                                            }))
                                        ]}
                                        activeKey={inspectorTool === 'ambient' ? activeAmbientTool : 'selected'}
                                        /* UNA VOCE PREMUTA A COLONNA CHIUSA LA RIAPRE: sceglierla vuol dire
                                           volerla guardare, e lasciarla chiusa sarebbe un comando che non
                                           risponde. */
                                        onSelect={(key) => {
                                            if (key === 'selected') {
                                                setInspectorTool('selected');
                                            } else {
                                                setAmbientTool(key);
                                                setInspectorTool('ambient');
                                            }
                                            if (isInspectorCollapsed) toggleCollapsed('right');
                                        }}
                                        expanded={preferences.sectionRails === 'expanded'}
                                        side='right'
                                        collapsed={isInspectorCollapsed}
                                        onToggleCollapse={() => toggleCollapsed('right')}
                                    />
                                )}
                            </InspectorPanel>
                        )}
                    </Workspace>
                    {/* Le sezioni che cambiano casa vivono qui, ferme nell'albero di React, e si
                        vedono dentro il nodo che trasloca. `SectionPlacementProvider` è l'unica
                        cosa che deve sapere di dove si trovano: la ricerca sale sulla riga dei
                        comandi quando è nella striscia, che è bassa e larga. */}
                    {ambientHostRef.current &&
                        ambientSection &&
                        createPortal(<ambientSection.component onInspectorChange={setInspectorContent} />, ambientHostRef.current)}
                    {libraryHostRef.current &&
                        librarySection &&
                        createPortal(
                            <SectionPlacementProvider value={showCentralLibrary ? 'strip' : 'panel'}>
                                <librarySection.component onInspectorChange={setInspectorContent} />
                            </SectionPlacementProvider>,
                            libraryHostRef.current
                        )}
                </MainContentContainer>
            </Container>
        </Load3DSceneEditor>
    );
};

export default SceneEditor;
